/**
 * Category-specific prompt templates for Radar action items.
 *
 * Each template produces a structured prompt that users can paste into
 * their AI coding agent (Claude, ChatGPT, Cursor) to implement fixes.
 * Templates use {placeholder} substitution filled by prompt-builder.ts.
 */

export interface PromptTemplate {
  category: string
  steps: string
  verification: string
}

export const PROMPT_TEMPLATES: Record<string, PromptTemplate> = {
  crawlability: {
    category: 'Crawlability',
    steps: `1. Open your robots.txt file (located at {domain}/robots.txt)
2. Review the current rules blocking AI bots: {blockedBots}
3. For each blocked bot, either:
   a. Remove the "Disallow: /" rule for that user-agent, OR
   b. Add an explicit "Allow: /" rule for pages you want AI to access
4. Add a Sitemap directive if missing: Sitemap: {domain}/sitemap.xml
5. If your site is a single-page app (SPA), enable server-side rendering for key pages so AI crawlers can read your content without JavaScript execution
6. Save and deploy the updated robots.txt`,
    verification: `After deploying your changes:
- Visit {domain}/robots.txt to confirm the new rules are live
- Re-run the Crawl Check tool in Radar to verify AI bots can now access your site
- Expected improvement: blocked bots should drop to 0 for AI browsing bots`,
  },

  'structured-data': {
    category: 'Structured Data',
    steps: `{structuredDataSteps}`,
    verification: `After deploying:
- Re-run the Schema Audit tool in Radar
- Target: a business identity, WebSite, BreadcrumbList where applicable, and a matching page type should show as "found"
- Target: completeness score above 70% for each detected type`,
  },

  'llm-communication': {
    category: 'LLM Communication',
    steps: `1. Create a file called llms.txt at your domain root ({domain}/llms.txt)
2. Structure it with these sections:
   - Title line: # {brand}
   - One-line description of what your company does
   - ## Company Overview - who you are, what you build, where you are based
   - ## Products/Services - list each with a one-line description and URL
   - ## Key URLs - links to your most important pages
   - ## Use Policy - how AI models should reference your content
3. {llmsSpecificAdvice}
4. If you already have llms.txt, create llms-full.txt with expanded descriptions, scoring methodologies, and comprehensive URL listings
5. Ensure your llms.txt content matches your JSON-LD and meta tags (cross-signal consistency)`,
    verification: `After deploying:
- Visit {domain}/llms.txt to confirm the file is accessible
- Re-run the llms.txt Validator tool in Radar
- Target: overall score above 60/100
- Check that all sections score above 50% of their max`,
  },

  citations: {
    category: 'Citations',
    steps: `1. Current citation state: {citationState}
2. Build topical authority in your niche:
   - Create 3-5 comprehensive, definitive guides on your core topics
   - Each guide should be 2000+ words with original data, insights, or frameworks
   - Use proper heading hierarchy (H1 > H2 > H3) with question-format headings
3. Strengthen entity signals:
   - Ensure consistent brand name (NAP) across all web properties
   - Add Organization schema with sameAs links to all official profiles
   - Get listed on industry directories and comparison sites
4. Build corroboration (Barnard's Corroboration Threshold):
   - Aim for 2-3 independent sources confirming key claims about your brand
   - Get mentioned in industry publications, podcasts, or guest posts
5. Target specific AI providers where you are not currently cited: {uncitedProviders}`,
    verification: `After implementing (allow 2-4 weeks for AI models to re-index):
- Re-run the Citations tool in Radar
- Target: mention rate above 30% across providers
- Monitor which providers cite you and which still do not`,
  },

  community: {
    category: 'Community Signals',
    steps: `1. Current Reddit state: {redditState}
2. Identify 3-5 relevant subreddits in your industry
3. Contribute genuinely helpful answers and insights (no self-promotion):
   - Answer questions with expertise and data
   - Share frameworks or mental models that help others
   - Link to your content only when directly relevant and helpful
4. {seedingAdvice}
5. Monitor brand mentions using Reddit search and set up alerts
6. Build organic mentions through value-first engagement over 4-8 weeks`,
    verification: `After 4-8 weeks of consistent engagement:
- Re-run the Reddit Monitor tool in Radar
- Target: at least 5+ organic mentions
- Check that seeding score stays at 0 (no detected artificial promotion)`,
  },

  aeo: {
    category: 'Answer Engine Optimization',
    steps: `1. Current AEO state: {aeoState}
2. Restructure content for AI extraction:
   - Rewrite H2 headings as questions users actually ask
   - Front-load definitions: first 1-2 sentences under each H2 must be a standalone, quotable answer
   - Keep first-paragraph answers under 300 characters
3. Add extractable data elements:
   - Convert comparison data to HTML tables (AI extracts tables verbatim)
   - Wrap key statistics in callout/highlight blocks with source attribution
   - Use ordered lists for step-by-step content
4. Add SpeakableSpecification to your page schema (Article for blog posts, WebPage for landing pages, FAQPage for FAQ pages):
   - Target your headline, description, and key takeaways elements
   - Use cssSelector to identify which content AI should quote
5. Ensure valid heading hierarchy: exactly one H1, followed by H2s, then H3s`,
    verification: `After deploying:
- Re-run the AEO Page Auditor tool in Radar
- Target: overall score above 60/100
- Focus areas: answer-first structure and data extractability scores`,
  },

  'answer-engine': {
    category: 'Citation Test',
    steps: `1. Current citation test state: {answerState}
2. Optimize your target page for the specific question: "{targetQuestion}"
3. Restructure the page:
   - The first paragraph must directly answer the question in under 300 characters
   - Add the question (or close variant) as an H2 heading
   - Include supporting data: statistics, comparisons, examples
4. Add supporting signals:
   - FAQPage schema with the target question and answer
   - SpeakableSpecification targeting the answer paragraph
   - Internal links from authoritative pages on your site
5. {competitorAdvice}
6. Build external signals: get the page linked from relevant industry content`,
    verification: `After deploying (allow 1-2 weeks):
- Re-run the Citation Test tool in Radar with the same question
- Target: at least one AI provider cites your page
- Compare against competitor URLs to understand remaining gaps`,
  },

  'source-influence': {
    category: 'Source Influence',
    steps: `1. Current state: {sourceState}
2. The top cited sources in your category are: {topSources}
3. Analyze what makes them citable:
   - Visit each top source and study their content format
   - Note: comprehensive guides, original data, clear structure, strong backlinks
4. Create category-definitive content:
   - Build the most comprehensive guide for your specific niche
   - Include original research, data, or frameworks that others will reference
   - Use proper structured data (Article schema with complete fields)
5. Build domain authority signals:
   - Get backlinks from topically relevant sites (not random link building)
   - Get cited in industry publications and comparison articles
6. Target the specific query types where top sources appear and you do not`,
    verification: `After 4-8 weeks:
- Re-run the Source Influence tool in Radar
- Target: your domain appears in the cited sources list
- Monitor which specific query types cite your content`,
  },

  sov: {
    category: 'Share of Voice',
    steps: `{sovSteps}`,
    verification: `After 4-8 weeks:
- Re-run the Prompt SOV tool in Radar using the same prompt/provider panel
- Compare SOV percentage, total brand mentions, and which providers mention your brand against this baseline
- Track changes over time using Radar history`,
  },

  hallucination: {
    category: 'Hallucination Correction',
    steps: `1. Current hallucination state: {hallucinationState}
2. Identify the specific false claims: {falseClaims}
3. Correct the source signals:
   - Update your homepage and About page with accurate, prominent facts
   - Add complete Organization schema: name, founding date, location, products
   - Add Product/Service schema with accurate names, descriptions, and pricing (if public)
4. Update your llms.txt with explicit corrections:
   - State correct facts clearly: "[Brand] was founded in [year]"
   - List actual products and services by name
   - If pricing is not public, state: "Pricing is not publicly available and should not be estimated"
5. Build corroboration: ensure 2-3 independent web sources state the correct facts
6. File correction requests with AI providers that support it (Perplexity, Google)`,
    verification: `After deploying (allow 2-4 weeks for model re-indexing):
- Re-run the Hallucination Check tool in Radar
- Target: high-severity hallucinations reduced to 0
- Monitor for new hallucinations that may appear as AI models update`,
  },
}
